import { createTheme, getContrastRatio, Theme } from "@mui/material/styles";
import { brandColors, buttonTokens, withAlpha } from "@trusty/shared";
import { COARSE_POINTER, touchTargetSx } from "./lib/a11y";

/**
 * ENG-2605: a Chip is a label, so hovering it should not show a text I-beam - which reads as "select
 * and copy this". MUI's Chip root sets `cursor: unset`, i.e. inherit, which resolves to the I-beam;
 * that is a component-level default, so the correction belongs here rather than on each callsite (the
 * package has 32 Chip render sites, and fixing them by hand had already missed two thirds of them).
 *
 * The `clickable` slot is restated because MUI emits slot overrides AFTER `root`, so without it a
 * root-level cursor would clobber the pointer on genuinely interactive chips. A deletable chip's `X`
 * keeps its own pointer: that rule is on a descendant, so it wins over the parent regardless.
 */
const CHIP_CURSOR = {
  MuiChip: {
    styleOverrides: {
      root: { cursor: "default" },
      clickable: { cursor: "pointer" },
    },
  },
} as const;

/**
 * ENG-3355: a Button's label is sentence case unless it says otherwise. MUI's default is uppercase
 * (via `typography.button`) and this theme never overrode it, so every Button whose author forgot the
 * per-callsite `textTransform: "none"` rendered "DENY" or "SAVE" beside sentence-case neighbours -
 * the same drift ENG-2605 hit with the Chip cursor above. An `sx` still wins over this, so a Button
 * that genuinely wants caps can still ask. Deliberately case ONLY: pill vs square shape is an open
 * design decision, not a default to settle here by accident.
 */
const BUTTON_CASE = {
  MuiButton: {
    styleOverrides: {
      // ENG-3356: and at least 40px tall under a finger (TOUCH_TARGETS), here because it is the same
      // root. Height only: MUI's own 64px minimum width stays, where touchTargetSx's 40 would undercut it.
      root: { textTransform: "none", [COARSE_POINTER]: { minHeight: 40 } },
    },
  },
} as const;

/**
 * ENG-3356: on a touch screen a button is at least 40×40 (`touchTargetSx`), whatever its call site
 * asked for. The console's small buttons were 25-37px tall under a thumb - Approve and Decline, the
 * row ⋮ menus at 32px, the remove icons at 30px - and fixing them one call site at a time is how
 * ENG-2605 and ENG-3355 ended up here. A mouse keeps the compact size. The Button's half is in
 * BUTTON_CASE, whose `root` it shares.
 */
const TOUCH_TARGETS = {
  MuiIconButton: { styleOverrides: { root: touchTargetSx } },
  MuiToggleButton: { styleOverrides: { root: touchTargetSx } },
  // The popup and clear buttons are IconButtons, so 40px wide on a touch screen, where MUI reserves
  // 26px each at the input's right. Reserve 40 (plus MUI's 9px inset and 4px gap) so the typed text
  // does not run under them. Doubled class: MUI's outlined rule carries three classes.
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        [COARSE_POINTER]: {
          "&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot.MuiAutocomplete-inputRoot, &.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot.MuiAutocomplete-inputRoot": { paddingRight: 53 },
          "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot.MuiAutocomplete-inputRoot": { paddingRight: 93 },
        },
      },
    },
  },
} as const;

/** Shallow-merges per-component theme entries, so two entries for one component don't clobber. */
const mergeComponents = (...parts: Record<string, object>[]): Record<string, object> =>
  parts.reduce<Record<string, object>>((acc, part) => {
    for (const [name, entry] of Object.entries(part)) acc[name] = { ...acc[name], ...entry };
    return acc;
  }, {});

/**
 * ENG-3480: MUI draws primary TEXT in `palette.primary.main` - links, the selected tab, text and
 * outlined buttons, outlined chips. In the dark theme that is Elm, and Elm as text on the dark canvas
 * and card measures 3.1-3.7:1, under WCAG AA's 4.5:1. Mint is already this theme's heading colour and
 * clears it by a wide margin (theme.contrast.test.tsx computes both from the palette).
 *
 * So the dark theme redirects those components' TEXT to Mint and leaves `primary.main` alone: Elm stays
 * the fill (contained buttons, switches, the tab indicator), where the label on it is white. Done here
 * rather than per call site for the reason CHIP_CURSOR gives. A call site that sets its own colour in
 * `sx` still wins, and must read `primaryTextColor(theme)` rather than `primary.main`.
 *
 * Light mode gets none of this: its primary is Eden, which already reads as text on the light ground.
 */
const primaryTextVariants = (text: string) => ({
  MuiLink: {
    variants: [{
      props: { color: "primary" },
      style: { color: text, "--Link-underlineColor": withAlpha(text, 0.4), textDecorationColor: withAlpha(text, 0.4) },
    }],
  },
  MuiTab: {
    variants: [{ props: { textColor: "primary" }, style: { "&.Mui-selected": { color: text } } }],
  },
  // Only the two variables the text and outlined variants read; contained reads its own.
  MuiButton: {
    variants: [{ props: { color: "primary" }, style: { "--variant-textColor": text, "--variant-outlinedColor": text } }],
  },
  MuiChip: {
    variants: [{ props: { variant: "outlined", color: "primary" }, style: { color: text } }],
  },
});

// Augment MUI's Palette to include custom tokens
declare module "@mui/material/styles" {
  interface Palette {
    custom: {
      inputBg: string;
      inputBorder: string;
      cardBorder: string;
      headingColor: string;
      outlinedBtnBorder: string;
      outlinedBtnHoverBg: string;
      dangerBorderColor: string;
      cardShadow: string;
      hoverShadow: string;
      chipDefaultBg: string;
      chipDefaultColor: string;
      noticeText: string;
      primaryText: string;
    };
  }
  interface PaletteOptions {
    custom?: {
      inputBg?: string;
      inputBorder?: string;
      cardBorder?: string;
      headingColor?: string;
      outlinedBtnBorder?: string;
      outlinedBtnHoverBg?: string;
      dangerBorderColor?: string;
      cardShadow?: string;
      hoverShadow?: string;
      chipDefaultBg?: string;
      chipDefaultColor?: string;
      noticeText?: string;
      primaryText?: string;
    };
  }
}

const sharedTypography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

export const lightTheme: Theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: buttonTokens.app.background, dark: buttonTokens.app.backgroundHover },
    // ENG-2530: warm mint-neutral base (the brand tint itself comes from the token
    // gradient in getDashboardBackground). `paper` stays opaque #FFFFFF so elevation
    // Papers — menus, dialogs — read solid; only the outlined card variant goes frosted.
    background: { default: "#EEF6EF", paper: "#FFFFFF" },
    text: {
      primary: "rgba(30, 30, 30, 1)",
      secondary: "rgba(30, 30, 30, 0.7)",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    error: { main: brandColors.red },
    custom: {
      inputBg: "#F8F8F8",
      inputBorder: "#F7F0F5",
      cardBorder: "#F0F0F0",
      headingColor: brandColors.forest,
      outlinedBtnBorder: withAlpha(brandColors.slate, 0.2),
      outlinedBtnHoverBg: withAlpha(brandColors.eden, 0.03),
      dangerBorderColor: "rgba(211, 47, 47, 0.3)",
      cardShadow: "0 1px 3px rgba(0,0,0,0.05)",
      hoverShadow: "0 4px 12px rgba(0,0,0,0.08)",
      chipDefaultBg: "rgba(0,0,0,0.08)",
      chipDefaultColor: "rgba(30,30,30,0.7)",
      // ENG-3189: caution text that is not an error - the amber notes, the at-risk count.
      // Dark is a LIGHTER amber, not the same hex: #92400E on the dark console ground measures
      // 2.31:1, so the text meant to draw the eye was the least readable thing on the card.
      // #F59E0B holds 6.11:1 on the lightest dark ground (top-centre, where the Elm and Plum
      // radials stack) and is already this app's dark amber - RiskReportPage's callout tints
      // with it at 10%. Always a bg+fg PAIR or a token; a bare hex assumes a ground it cannot see.
      noticeText: "#92400E",
      // ENG-3480: primary as TEXT. Light is primary itself (Eden), unchanged.
      primaryText: buttonTokens.app.background,
    },
  },
  typography: sharedTypography,
  components: {
    // ENG-2530: warm the console's cards without touching menus/dialogs. `variant="outlined"`
    // is the card surface across every page; MUI Menu/Dialog use ELEVATION Papers, so this
    // override never reaches them. Translucent white over the token gradient reads frosted;
    // the hairline is a Forest tint (brand-derived), not flat grey.
    MuiPaper: {
      styleOverrides: {
        outlined: { backgroundColor: "rgba(255,255,255,0.72)", borderColor: withAlpha(brandColors.forest, 0.08) },
      },
    },
    ...CHIP_CURSOR,
    ...BUTTON_CASE,
    ...TOUCH_TARGETS,
  },
});

export const darkTheme: Theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#1A7A82", dark: "#0F5257" },
    // ENG-2530: forest-tinted dark base; brand warmth from the token gradient. `paper`
    // stays a solid near-black so menus/dialogs read opaque (only outlined cards go frosted).
    background: { default: "#0E1512", paper: "#1E1E1E" },
    text: {
      primary: "rgba(255, 255, 255, 0.95)",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
    divider: "rgba(255, 255, 255, 0.12)",
    error: { main: "#f44336" },
    custom: {
      inputBg: "#2A2A2A",
      inputBorder: "rgba(255, 255, 255, 0.12)",
      cardBorder: "rgba(255, 255, 255, 0.12)",
      headingColor: brandColors.mint,
      outlinedBtnBorder: "rgba(255, 255, 255, 0.23)",
      outlinedBtnHoverBg: withAlpha(brandColors.elm, 0.12),
      dangerBorderColor: "rgba(244, 67, 54, 0.5)",
      cardShadow: "0 1px 3px rgba(0,0,0,0.3)",
      hoverShadow: "0 4px 12px rgba(0,0,0,0.4)",
      chipDefaultBg: "rgba(255,255,255,0.12)",
      chipDefaultColor: "rgba(255,255,255,0.7)",
      noticeText: "#F59E0B",
      // ENG-3480: Mint, not Elm - see primaryTextVariants.
      primaryText: brandColors.mint,
    },
  },
  typography: sharedTypography,
  components: {
    // ENG-2530 (dark parity): frosted translucent card over the dark token gradient,
    // Mint-tint hairline. Menus/dialogs (elevation Papers) are untouched.
    MuiPaper: {
      styleOverrides: {
        outlined: { backgroundColor: "rgba(255,255,255,0.05)", borderColor: withAlpha(brandColors.mint, 0.10) },
      },
    },
    ...mergeComponents(CHIP_CURSOR, BUTTON_CASE, TOUCH_TARGETS, primaryTextVariants(brandColors.mint)),
  },
});

// ENG-3355: the brand stops are `${}`-interpolated. ENG-2533 left them as bare `withAlpha(…)` text in
// this template, which is not CSS, so the browser dropped the whole `background` and the dark loading
// screens (getLoginGradient) rendered with no gradient at all. lib/with-alpha.guard.test.ts holds it.
export const DARK_GRADIENT_BACKGROUND = `
  radial-gradient(ellipse 1200px 1000px at 20% 60%, ${withAlpha(brandColors.eden, 0.5)} 0%, transparent 50%),
  radial-gradient(ellipse 1000px 900px at 80% 40%, rgba(64, 31, 62, 0.4) 0%, transparent 55%),
  radial-gradient(ellipse 900px 800px at 10% 20%, ${withAlpha(brandColors.forest, 0.5)} 0%, transparent 50%),
  radial-gradient(ellipse 1100px 1000px at 90% 80%, rgba(107, 72, 105, 0.35) 0%, transparent 55%),
  radial-gradient(ellipse 1300px 1100px at 50% 100%, ${withAlpha(brandColors.elm, 0.25)} 0%, transparent 60%),
  linear-gradient(135deg, #0F5257 0%, #1A7A82 50%, #401F3E 100%)
`;

/**
 * ENG-3189 — read the caution-text colour through this, never `palette.custom.noticeText` directly.
 *
 * `custom` is an app-defined palette key, so a bare `createTheme()` does not have it — and twelve
 * test files build exactly that, stubbing `custom` as a hand-listed allowlist of the tokens their
 * page happened to read on the day they were written. Adding a token therefore arms a crash in every
 * test whose stub predates it: `noticeText` took out three suites the moment ListControls read it.
 * Patching those stubs moves the landmine rather than removing it.
 *
 * The fallback is not papering over a missing value — it is the same pair the themes declare, so a
 * component rendered under any theme gets the mode-correct colour instead of throwing.
 */
export const noticeTextColor = (t: Theme): string =>
  t.palette.custom?.noticeText ?? (t.palette.mode === "dark" ? "#F59E0B" : "#92400E");

/**
 * ENG-3480 — primary as TEXT, for a call site that sets its own colour in `sx` (which the theme's
 * overrides cannot reach). Elm in dark mode, which `primary.main` gives, is under 4.5:1 there.
 * Falls back like `noticeTextColor`, for the bare `createTheme()` many tests render under.
 */
export const primaryTextColor = (t: Theme): string =>
  t.palette.custom?.primaryText ?? (t.palette.mode === "dark" ? brandColors.mint : t.palette.primary.main);

/**
 * ENG-3480 — the label colour for a filled chip or badge, chosen from its fill: white or near-black,
 * whichever contrasts more. A fixed white label assumes a dark fill, and MUI's success and warning
 * fills are light in dark mode (white on its green measured 2.36:1). MUI's own `getContrastText`
 * settles for white at 3:1, which is under the 4.5:1 a chip's small label needs, so it is not used.
 */
const READABLE_DARK = "#1E1E1E";
export const readableOn = (fill: string): string =>
  getContrastRatio("#FFFFFF", fill) >= getContrastRatio(READABLE_DARK, fill) ? "#FFFFFF" : READABLE_DARK;
